import React from "react";
import { Grid } from "@material-ui/core";
import { observable } from "mobx";
import { observer, inject } from "mobx-react";
import SimpleModal from "@src/Components/Common/SimpleModal/SimpleModal";
import { IManagementStore } from "Stores/Management/types";
import { IValuesStore } from "Stores/Values/types";
import AddIcon from "@material-ui/icons/Add";
import ThemeForm from "../ThemeForm/ThemeForm";
import { IThemesStore } from "@src/Stores/Themes/types";
import { Theme } from "@src/Interfaces/Entity/Theme";
import { IFile } from "@src/Interfaces/Entity/File";
import { IAuthStore } from "@src/Stores/Auth/types";
import { Values } from "@src/Tools/Values";
import RoundButton from "@src/Shared/Components/RoundButton/RoundButton";

interface IProps {
  management?: IManagementStore;
  values?: IValuesStore;
  themes?: IThemesStore;
  auth?: IAuthStore;
}

@inject("auth", "management", "values", "themes")
@observer
class AddTheme extends React.Component<IProps> {
  @observable opened: boolean = false;

  handleOpen = () => {
    this.opened = true;
  };

  handleClose = () => {
    this.opened = false;
  };

  handleResetOrUploadImage = async (image: File, objectId: string, fileType: number) => {
    if (!this.isImageChanged(image)) {
      return "00000000-0000-0000-0000-000000000000";
    }
    if (image.name === "reset") {
      return null;
    }
    const file: IFile = await this.props.management.uploadFile(image, objectId, fileType);
    return file.id;
  };

  handleImageChanged = async (oldTheme: Theme, newTheme: Theme) => {
    const {
      ApplicationLogoImage,
      LoginBackgroundLandscapeImage,
      LoginBackgroundPortraitImage,
      LoginLogoImage,
    } = this.props.values.fileTypes;
    if (oldTheme.type === this.props.values.themeTypes.Application) {
      newTheme.applicationThemeDetails.applicationLogoImageId = await this.handleResetOrUploadImage(
        oldTheme.applicationThemeDetails.applicationLogoImage,
        newTheme.id,
        ApplicationLogoImage
      );
    } else {
      newTheme.loginThemeDetails.loginBackgroundLandscapeImageId = await this.handleResetOrUploadImage(
        oldTheme.loginThemeDetails.loginBackgroundLandscapeImage,
        newTheme.id,
        LoginBackgroundLandscapeImage
      );
      newTheme.loginThemeDetails.loginBackgroundPortraitImageId = await this.handleResetOrUploadImage(
        oldTheme.loginThemeDetails.loginBackgroundPortraitImage,
        newTheme.id,
        LoginBackgroundPortraitImage
      );
      newTheme.loginThemeDetails.loginLogoImageId = await this.handleResetOrUploadImage(
        oldTheme.loginThemeDetails.loginLogoImage,
        newTheme.id,
        LoginLogoImage
      );
    }
    return newTheme;
  };

  isImageChanged = (image: File) => !(image.name === "default" && image.size === 0);

  handleCreate = async (values: Theme) => {
    try {
      let newTheme: Theme = await this.props.management.saveTheme(values, true);
      const isApplicationTheme = newTheme.type == this.props.values.themeTypes.Application;
      const isAnyImageChanged =
        this.isImageChanged(values.loginThemeDetails.loginBackgroundLandscapeImage) ||
        this.isImageChanged(values.loginThemeDetails.loginBackgroundPortraitImage) ||
        this.isImageChanged(values.loginThemeDetails.loginLogoImage) ||
        this.isImageChanged(values.applicationThemeDetails.applicationLogoImage);
      if (isAnyImageChanged) {
        newTheme = await this.handleImageChanged(values, newTheme);
        await this.props.management.saveTheme(newTheme);
      }
      if (isApplicationTheme && newTheme.state === this.props.values.states.Active) {
        await this.props.themes.getCurrentTheme(this.props.values.themeTypes.Application);
      }
      await this.props.management.getThemes();
    } catch (error) {
      console.error(error);
    }
    this.handleClose();
  };

  render() {
    const theme: Theme = new Theme();
    theme.locationId = this.props.auth.user.location.id;

    return (
      <Grid item>
        <RoundButton
            icon={<AddIcon/>}
            tooltipTitle={Values.getLocalizationValue("add")}
            onClick={() => this.handleOpen()}
            color={"green"}
        />
        
        <SimpleModal width={500} isOpened={this.opened} onClose={this.handleClose}>
          <ThemeForm
            title={Values.getLocalizationValue("add")}
            initialValues={theme}
            onSubmit={this.handleCreate}
            onClose={this.handleClose}
            isEdit={false}
          />
        </SimpleModal>
      </Grid>
    );
  }
}

export default AddTheme;
